import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from 'urql'
import { GET_USER, GET_AVAILABLE_MODULES, UPDATE_MODULE_PERMISSION, UPDATE_USER_HISTORY_COMMENT, TOGGLE_USER_ACTIVE, DELETE_USER, UPDATE_USER_BLOCKED_FEATURES, DELETE_USER_HISTORY_COMMENT } from '../../graphql/users'
import { GET_COMPANIES, GET_COMPANIES_BY_USER, BULK_ADD_USER_TO_COMPANIES, BULK_REMOVE_USER_FROM_COMPANIES } from '../../graphql/companies'
import './dashboard.css'
import { resolveModuleHasAccess } from '../../utils/resolveModuleHasAccess'
import { isRepositorioParentModuleId, withoutRepositorioParentPermission } from '../../constants/repositorioParentModule'
import {
  MODULE_GRANT_FEATURES,
  MODULE_GRANT_LABELS,
  MODULE_GRANT_MODULE_IDS,
  isModuleGrantModule,
} from '../../constants/moduleGrantFeatures'
import { EXPORT_BLOCKABLE_FEATURES } from '../../constants/exportBlockableFeatures'

// Componente para editar comentarios de historial
function HistoryComment({ comment, commentAuthorEmail, currentUserEmail, onSave, onDelete, disabled }) {
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(comment || '')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Verificar si el usuario actual puede editar/eliminar (es el autor o no hay autor aún)
  const canEdit = !disabled && (!commentAuthorEmail || commentAuthorEmail === currentUserEmail)
  const canDelete = !disabled && comment && commentAuthorEmail === currentUserEmail

  const handleSave = async () => {
    setSaving(true)
    await onSave(value)
    setSaving(false)
    setIsEditing(false)
  }

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      // Auto-cancelar después de 3 segundos
      setTimeout(() => setConfirmDelete(false), 3000)
      return
    }
    setDeleting(true)
    setConfirmDelete(false)
    onDelete().finally(() => setDeleting(false))
  }

  const handleCancel = () => {
    setValue(comment || '')
    setIsEditing(false)
  }

  if (disabled) {
    return comment ? (
      <div className="history-comment">
        <div className="comment-content">
          <span className="comment-text">{comment}</span>
          {commentAuthorEmail && (
            <span className="comment-author">— {commentAuthorEmail}</span>
          )}
        </div>
      </div>
    ) : null
  }

  if (isEditing) {
    return (
      <div className="history-comment editing">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Agregar comentario..."
          className="comment-input"
          autoFocus
        />
        <div className="comment-actions">
          <button onClick={handleCancel} disabled={saving} className="comment-btn cancel">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="comment-btn save">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="history-comment">
      {comment ? (
        <div className="comment-content">
          <span className="comment-text">{comment}</span>
          {commentAuthorEmail && (
            <span className="comment-author">— {commentAuthorEmail}</span>
          )}
        </div>
      ) : (
        <span className="comment-placeholder">Sin comentario</span>
      )}
      <div className="comment-buttons">
        {canEdit && (
          <button 
            onClick={() => setIsEditing(true)} 
            className="comment-edit-btn"
            title="Editar comentario"
          >
            Editar
          </button>
        )}
        {canDelete && (
          <button 
            onClick={handleDelete} 
            className="comment-delete-btn"
            title={confirmDelete ? "Haz click de nuevo para confirmar" : "Eliminar comentario"}
            disabled={deleting}
          >
            {deleting ? '...' : confirmDelete ? '¿Confirmar?' : 'Eliminar'}
          </button>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status, size = 'large' }) {
  const statusMap = {
    ACTIVO: { class: 'activo', label: 'Activo' },
    DESACTIVADO: { class: 'desactivado', label: 'Desactivado' },
  }

  const statusInfo = statusMap[status] || statusMap.ACTIVO
  const badgeClass = size === 'small' ? 'status-badge' : 'status-badge-large'

  return (
    <span className={`${badgeClass} ${statusInfo.class}`}>
      <span className="status-dot"></span>
      {statusInfo.label}
    </span>
  )
}

// Opciones de estado de usuario
const USER_STATUS_OPTIONS = [
  { value: 'ACTIVO', label: 'Activo', description: 'Usuario con acceso normal al sistema', color: '#27ae60' },
  { value: 'DESACTIVADO', label: 'Desactivado', description: 'Usuario sin acceso al sistema', color: '#e74c3c' },
]

// Funcionalidades que se pueden bloquear para usuarios (sin exportaciones)
const CORE_USER_BLOCKABLE_FEATURES = [
  {
    id: 'conciliacion',
    label: 'Bloquear Conciliación',
    description: 'Oculta la sección de Conciliación Bancaria y todos los botones "Conciliar" en el portal para este usuario',
    items: [
      'Menú lateral: Operaciones → Conciliación Bancaria',
      'Botones "Conciliar" en documentos tributarios',
      'Botones "Conciliar" en transacciones',
      'Modal de vinculación de documentos',
    ],
  },
  {
    id: 'nomina',
    label: 'Bloquear Generación de Nómina',
    description: 'Oculta el botón "Generar Nómina de Pagos" en Pago a Proveedores para este usuario',
    items: [
      'Botón "Generar Nómina de Pagos" en sección Por Pagar',
    ],
  },
  {
    id: 'asiento',
    label: 'Bloquear Nuevo Asiento',
    description: 'Oculta los botones "Nuevo asiento" y "Crear asiento" en el portal para este usuario',
    items: [
      'Botón "Nuevo asiento" en el menú + del header',
      'Botón "Nuevo asiento" en Conciliación Contabilizar',
      'Icono "Crear asiento" en items conciliados',
    ],
  },
  ...MODULE_GRANT_FEATURES,
  {
    id: 'f29',
    label: 'Bloquear F29',
    description: 'Oculta los botones "Declarar automáticamente" y "Enviar por Email" en Formulario 29 para este usuario',
    items: [
      'Botón "Declarar automáticamente" en F29',
      'Botón "Enviar por Email" en F29',
    ],
  },
  {
    id: 'pascale_chat',
    label: 'Bloquear Chat Pascale',
    description: 'Oculta el asistente Pascale (icono morado en el header). Las cuentas nuevas lo tienen bloqueado por defecto; quita el bloqueo aquí y en la empresa para habilitar',
    items: [
      'Botón del asistente Pascale en la barra superior',
      'Panel lateral de chat con Pascale',
    ],
  },
  {
    id: 'crear_obligacion',
    label: 'Bloquear Crear Obligación',
    description: 'Impide crear obligaciones manualmente o por importación masiva en el portal',
    items: [
      'Header: Crear → Nueva obligación',
      'Flujo de caja: estimación, importar obligaciones, acciones de área',
      '/obligaciones y /pago_proveedores: crear e importar',
      'Conciliación, transacciones, reconciliar, EERR y formularios relacionados',
      'No afecta reglas automáticas de conciliación (CREATE_OBLIGATION)',
    ],
  },
  {
    id: 'abonar',
    label: 'Bloquear Abonar',
    description: 'Impide registrar abonos y saldos de documentos con métodos de pago',
    items: [
      'Dropdown Abonar / PaymentMethodDropdown',
      'Pago a proveedores: abonar documento',
      'Obligaciones y DTEs: abono masivo y menú Abonar por fila',
      'Sidebar reconciliar documento',
    ],
  },
  {
    id: 'cargar_plan_cuenta',
    optIn: true,
    label: 'Cargar Plan de Cuenta',
    description: 'Permite importar el plan de cuentas desde Excel en /plan_de_cuentas. Debe estar activo (id en permisos) para ver la opción de carga.',
    items: [
      'Barra lateral en Plan de Cuentas → Importar Plan de Cuentas',
    ],
  },
  {
    id: 'cargar_repositorio',
    optIn: true,
    label: 'Cargar en Repositorio',
    description: 'Permite subir archivos en cualquier sección del Repositorio. ADMIN y SUPER_COLABORADOR lo tienen por defecto.',
    items: [
      'Botón Cargar en cada período del Repositorio',
      'Modal de carga de archivos',
    ],
  },
  {
    id: 'notificar_repositorio',
    optIn: true,
    label: 'Notificar en Repositorio',
    description: 'Permite notificar y entregar documentos del Repositorio por email. ADMIN y SUPER_COLABORADOR lo tienen por defecto.',
    items: [
      'Botón Notificar en cada período del Repositorio',
      'Modal de notificación por email',
    ],
  },
  {
    id: 'editar_saldos_flujo_caja',
    optIn: true,
    label: 'Editar saldos de Flujo de Caja',
    description: 'Permite editar saldos bancarios y saldos agregados en Flujo de Caja, e incluirlos o excluirlos. Agregar un saldo no usa este permiso. ADMIN, SUPER_COLABORADOR y COLABORADOR lo tienen por defecto.',
    items: [
      'Lápiz y switch "Incluir en flujo de caja" de cuentas bancarias',
      'Lápiz y switch de saldos agregados',
    ],
  },
]

const EXPORT_USER_BLOCKABLE_FEATURES = EXPORT_BLOCKABLE_FEATURES.map((feature) => ({
  id: feature.id,
  optIn: !!feature.optIn,
  label: feature.label,
  parentModule: feature.parentModule,
}))

const USER_BLOCKABLE_FEATURES = [...CORE_USER_BLOCKABLE_FEATURES, ...EXPORT_USER_BLOCKABLE_FEATURES]

const USER_BLOCKABLE_FEATURE_SECTIONS = [
  { id: 'core', title: 'FUNCIONALIDADES', features: CORE_USER_BLOCKABLE_FEATURES },
  { id: 'export', title: 'EXPORTACIONES SENSIBLES', features: EXPORT_USER_BLOCKABLE_FEATURES },
]

const OPT_IN_FEATURE_IDS = new Set(
  USER_BLOCKABLE_FEATURES.filter((f) => f.optIn).map((f) => f.id),
)

// Features opt-in concedidas por rol (no editables en el panel).
// En línea con el runtime (smart_back helpers / smart_front useBlockedFeatures).
const ROLE_GRANTED_OPT_IN_BY_FEATURE = {
  cargar_repositorio: ['ADMIN', 'SUPER_COLABORADOR', 'DPO'],
  notificar_repositorio: ['ADMIN', 'SUPER_COLABORADOR', 'DPO'],
  editar_saldos_flujo_caja: ['ADMIN', 'SUPER_COLABORADOR', 'COLABORADOR'],
}

function rolesGrantingFeature(feature) {
  if (!feature?.optIn) return null
  return ROLE_GRANTED_OPT_IN_BY_FEATURE[feature.id] || null
}

// Feature opt-in concedida automáticamente por el rol del usuario (no editable en el panel).
function featureGrantedByRole(feature, roles) {
  const granting = rolesGrantingFeature(feature)
  if (!granting || !Array.isArray(roles)) return false
  return roles.some((role) => granting.includes(role))
}

function grantedRoleLabel(feature, roles) {
  const granting = rolesGrantingFeature(feature) || []
  return granting.find((role) => Array.isArray(roles) && roles.includes(role)) || granting[0] || ''
}

function hasParentModuleAccess(parentModuleId, user) {
  if (!parentModuleId || !user) return true
  const perm = user.modulePermissions?.find((p) => p.moduleId === parentModuleId)
  return resolveModuleHasAccess(perm, parentModuleId, true, user.roles)
}

function normalizeBlockedFeaturesForModal(userBlockedFeatures) {
  if (!Array.isArray(userBlockedFeatures)) {
    return []
  }
  return [...userBlockedFeatures]
}

function featureHasAccess(feature, blockedFeatures, roles, user) {
  if (feature?.moduleGrant) {
    const perm = (user?.modulePermissions || []).find((p) => p.moduleId === feature.id)
    return resolveModuleHasAccess(perm, feature.id, true, roles)
  }
  if (featureGrantedByRole(feature, roles)) {
    return true
  }
  const list = Array.isArray(blockedFeatures) ? blockedFeatures : []
  if (feature.optIn) {
    return list.includes(feature.id)
  }
  if (feature.parentModule && user && !hasParentModuleAccess(feature.parentModule, user)) {
    return false
  }
  return !list.includes(feature.id)
}

function featureDisplayName(feature) {
  if (feature.optIn || feature.moduleGrant) return feature.label
  return feature.label.replace(/^Bloquear exportación /, '').replace(/^Bloquear API: /, 'API ').replace(/^Bloquear /, '')
}

function buildModuleGrantAccessMap(modulePermissions, roles) {
  const map = {}
  for (const moduleId of MODULE_GRANT_MODULE_IDS) {
    const perm = (modulePermissions || []).find((p) => p.moduleId === moduleId)
    map[moduleId] = resolveModuleHasAccess(perm, moduleId, true, roles)
  }
  return map
}

// Modal para cambiar estado de usuario
function UserStatusModal({ user, onClose, onConfirm, onDelete }) {
  const [selectedStatus, setSelectedStatus] = useState(user?.adminStatus || 'ACTIVO')
  const [saving, setSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [deleting, setDeleting] = useState(false)

  const userEmail = user?.emails?.[0]?.address || 'este usuario'
  const isConfirmValid = confirmEmail === userEmail

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (selectedStatus === user?.adminStatus) {
      onClose()
      return
    }
    setSaving(true)
    await onConfirm(selectedStatus)
    setSaving(false)
  }

  const handleDelete = async (e) => {
    e.preventDefault()
    if (!isConfirmValid) return
    
    setDeleting(true)
    try {
      await onDelete(deleteReason)
    } finally {
      setDeleting(false)
    }
  }

  // Vista de confirmación de eliminación
  if (showDeleteConfirm) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Eliminar Usuario</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <form onSubmit={handleDelete}>
            <div className="modal-body" style={{ boxSizing: 'border-box' }}>
              <div className="warning-box" style={{ marginBottom: '1.5rem' }}>
                <strong>ACCIÓN IRREVERSIBLE</strong>
                <p style={{ margin: '0.5rem 0 0 0' }}>
                  Esta acción eliminará permanentemente al usuario y todos sus datos asociados. 
                  Esta operación <strong>no se puede deshacer</strong>.
                </p>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">
                  Usuario a eliminar:
                </label>
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div className="user-email">{userEmail || 'Sin email'}</div>
                  <div className="user-name">
                    {user?.profile?.firstName || ''} {user?.profile?.lastName || ''}
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem', width: '100%', boxSizing: 'border-box' }}>
                <label className="form-label">
                  Razón de la eliminación (opcional):
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Ej: Cuenta duplicada, usuario solicita baja, etc."
                  className="form-textarea"
                  style={{ 
                    minHeight: '80px',
                    width: '100%',
                    boxSizing: 'border-box',
                    maxWidth: '100%'
                  }}
                />
              </div>

              <div className="form-group" style={{ width: '100%', boxSizing: 'border-box' }}>
                <label className="form-label" style={{ color: '#dc2626' }}>
                  Para confirmar, escribe el email del usuario:
                </label>
                <input
                  type="text"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={userEmail}
                  className="form-input"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    borderWidth: '2px',
                    borderColor: confirmEmail ? (isConfirmValid ? '#10b981' : '#dc2626') : '#e2e8f0',
                    backgroundColor: confirmEmail ? (isConfirmValid ? '#f0fdf4' : '#fef2f2') : 'white'
                  }}
                />
                {confirmEmail && !isConfirmValid && (
                  <span style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    El email no coincide
                  </span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="action-btn secondary" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Volver
              </button>
              <button 
                type="submit" 
                className="action-btn danger" 
                disabled={deleting || !isConfirmValid}
              >
                {deleting ? 'Eliminando...' : 'Eliminar Permanentemente'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Vista principal de cambio de estado
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Gestionar Usuario</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
              <div className="user-email" style={{ marginBottom: '0.25rem' }}>{userEmail}</div>
              <div className="user-name">
                {user?.profile?.firstName || ''} {user?.profile?.lastName || ''}
              </div>
            </div>
            
            <p style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.9rem' }}>
              Selecciona el estado para este usuario:
            </p>
            
            <div className="status-options">
              {USER_STATUS_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`status-option ${selectedStatus === option.value ? 'selected' : ''}`}
                  style={{ borderColor: selectedStatus === option.value ? option.color : undefined }}
                >
                  <input
                    type="radio"
                    name="adminStatus"
                    value={option.value}
                    checked={selectedStatus === option.value}
                    onChange={() => setSelectedStatus(option.value)}
                  />
                  <div className="status-option-info">
                    <span className="status-option-label" style={{ color: option.color }}>
                      {option.label}
                    </span>
                    <span className="status-option-desc">{option.description}</span>
                  </div>
                </label>
              ))}
              
              {/* Opción de eliminar con mismo estilo */}
              <label
                className={`status-option ${selectedStatus === 'ELIMINAR' ? 'selected' : ''}`}
                style={{ 
                  borderColor: selectedStatus === 'ELIMINAR' ? '#64748b' : '#e2e8f0',
                  marginTop: '0.5rem',
                  borderStyle: 'dashed'
                }}
              >
                <input
                  type="radio"
                  name="adminStatus"
                  value="ELIMINAR"
                  checked={selectedStatus === 'ELIMINAR'}
                  onChange={() => setSelectedStatus('ELIMINAR')}
                />
                <div className="status-option-info">
                  <span className="status-option-label" style={{ color: '#64748b' }}>
                    Eliminar
                  </span>
                  <span className="status-option-desc">Eliminar usuario permanentemente de la base de datos</span>
                </div>
              </label>
            </div>

            {selectedStatus === 'DESACTIVADO' && (
              <div className="warning-box warning-yellow" style={{ marginTop: '1rem' }}>
                <strong>Advertencia:</strong> Al desactivar este usuario, 
                <strong> no podrá acceder al portal</strong>.
              </div>
            )}

            {selectedStatus === 'ELIMINAR' && (
              <div className="warning-box" style={{ marginTop: '1rem' }}>
                <strong>ACCIÓN IRREVERSIBLE:</strong> Al eliminar este usuario, 
                <strong> se borrarán permanentemente todos sus datos</strong>. Esta acción no se puede deshacer.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="action-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            {selectedStatus === 'ELIMINAR' ? (
              <button 
                type="button"
                className="action-btn danger" 
                onClick={() => setShowDeleteConfirm(true)}
              >
                Continuar con Eliminación
              </button>
            ) : (
              <button 
                type="submit" 
                className="action-btn primary" 
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

// Modal para configurar Permisos de Funciones
function FunctionsPermissionsModal({ user, isOpen, onClose, onSave }) {
  const [saving, setSaving] = useState(false)
  const [localBlockedFeatures, setLocalBlockedFeatures] = useState(() =>
    normalizeBlockedFeaturesForModal(user?.blockedFeatures),
  )
  const [localModuleGrants, setLocalModuleGrants] = useState(() =>
    buildModuleGrantAccessMap(user?.modulePermissions, user?.roles),
  )
  const initialModuleGrantsRef = useRef(localModuleGrants)

  useEffect(() => {
    if (isOpen) {
      setLocalBlockedFeatures(normalizeBlockedFeaturesForModal(user?.blockedFeatures))
      const grants = buildModuleGrantAccessMap(user?.modulePermissions, user?.roles)
      setLocalModuleGrants(grants)
      initialModuleGrantsRef.current = { ...grants }
    }
  }, [isOpen, user?.blockedFeatures, user?.modulePermissions, user?.roles])

  const handleToggleFeature = (feature) => {
    if (featureGrantedByRole(feature, user?.roles)) return
    if (feature.moduleGrant) {
      setLocalModuleGrants((prev) => ({
        ...prev,
        [feature.id]: !prev[feature.id],
      }))
      return
    }

    const featureId = feature.id
    const isGrant = OPT_IN_FEATURE_IDS.has(featureId)
    const isInList = localBlockedFeatures.includes(featureId)

    if (isGrant) {
      setLocalBlockedFeatures(
        isInList
          ? localBlockedFeatures.filter((f) => f !== featureId)
          : [...localBlockedFeatures, featureId],
      )
      return
    }

    setLocalBlockedFeatures(
      isInList
        ? localBlockedFeatures.filter((f) => f !== featureId)
        : [...localBlockedFeatures, featureId],
    )
  }

  const handleSave = async () => {
    setSaving(true)
    const moduleGrantPatches = MODULE_GRANT_MODULE_IDS
      .filter((moduleId) => localModuleGrants[moduleId] !== initialModuleGrantsRef.current[moduleId])
      .map((moduleId) => ({
        moduleId,
        hasAccess: !!localModuleGrants[moduleId],
        excludedFeatures: [],
      }))
    const ok = await onSave({ blockedFeatures: localBlockedFeatures, moduleGrantPatches })
    setSaving(false)
    if (ok) onClose()
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content permissions-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Permisos de Funciones</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="modal-description">
            Configura el acceso a funcionalidades específicas para este usuario.
          </p>
          
          <div className="permissions-list">
            {USER_BLOCKABLE_FEATURE_SECTIONS.map((section) => (
              <div key={section.id} className="permission-category">
                <h4 className="category-title">{section.title}</h4>
                <div className="category-modules">
                  {section.features.map((feature) => {
                    const grantedByRole = featureGrantedByRole(feature, user?.roles)
                    const parentModuleBlocked =
                      !!feature.parentModule && !hasParentModuleAccess(feature.parentModule, user)
                    const hasAccess = feature.moduleGrant
                      ? !!localModuleGrants[feature.id]
                      : featureHasAccess(
                          feature,
                          localBlockedFeatures,
                          user?.roles,
                          user,
                        )

                    return (
                      <div key={feature.id} className={`permission-module ${!hasAccess ? 'disabled' : ''}`}>
                        <div className="permission-header">
                          <label className="permission-toggle">
                            <input
                              type="checkbox"
                              checked={hasAccess}
                              disabled={grantedByRole || parentModuleBlocked}
                              onChange={() => handleToggleFeature(feature)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <div className="permission-info">
                            <span className="permission-name">{featureDisplayName(feature)}</span>
                            {parentModuleBlocked && (
                              <span className="permission-role-hint">
                                Requiere módulo {getModuleName(feature.parentModule)}
                              </span>
                            )}
                            {grantedByRole && (
                              <span className="permission-role-hint">
                                Incluido por rol ({grantedRoleLabel(feature, user?.roles)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="action-btn secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button className="action-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Permisos'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Estructura de carpetas con sub-módulos (compartida entre PermissionsModal y UserDetail)
const MODULE_CATEGORIES = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    subModules: []
  },
  {
    id: 'onboarding',
    name: 'Onboarding',
    subModules: [{ id: 'softlanding', name: 'Configuración Inicial' }],
  },
  {
    id: 'reportes',
    name: 'Reportes',
    subModules: [
      { id: 'reportes_ventas', name: 'Reporte de Ventas' },
      { id: 'reportes_compras', name: 'Reporte de Compras' },
      { id: 'reportes_cobranza', name: 'Reporte de Cobranza' },
      { id: 'reportes_proveedores', name: 'Reporte de Proveedores' },
      { id: 'reportes_dj', name: 'Reporte DDJJ' },
      { id: 'reportes_rrhh', name: 'Reporte RRHH' },
      { id: 'reportes_personalizados', name: 'Personalizados' },
    ]
  },
  {
    id: 'finanzas',
    name: 'Finanzas',
    subModules: [
      { id: 'flujo_caja', name: 'Flujo de Caja' },
      { id: 'eerr', name: 'Estado de Resultados de Gestión' },
      { id: 'eoaf', name: 'EOAF' },
      { id: 'creditos', name: 'Préstamos' },
      { id: 'estructuras_maestras', name: 'Estructuras Maestras (configuración)' },
      { id: 'estado_de_resultados_gestion', name: 'EERR por Estructura Maestra' },
      { id: 'balance_clasificado_gestion', name: 'Balance Clasificado por Estructura Maestra' },
    ]
  },
  {
    id: 'documentos',
    name: 'Documentos',
    subModules: [
      { id: 'documentos_tributarios', name: 'DTEs' },
      { id: 'boletas', name: 'Boletas de Honorarios' },
      { id: 'obligaciones', name: 'Obligaciones' },
      { id: 'cuotas_creditos', name: 'Cuotas de Créditos' },
      { id: 'din', name: 'Declaraciones de Ingreso' },
    ]
  },
  {
    id: 'operaciones',
    name: 'Operaciones',
    subModules: [
      { id: 'conciliacion', name: 'Conciliación Bancaria' },
      { id: 'cobranza', name: 'Cobranza' },
      { id: 'pago_proveedores', name: 'Pago a Proveedores' },
      { id: 'cuadre_cartolas', name: 'Cuadre de Cartolas' },
      { id: 'tarjetas_credito', name: 'Tarjetas de Crédito' },
    ]
  },
  {
    id: 'impuestos',
    name: 'Impuestos',
    subModules: [
      { id: 'formulario_29', name: 'Formulario 29' },
      { id: 'consolidado_f29', name: 'Consolidado F29' },
      { id: 'multas_sii', name: 'Multas en el SII' },
      { id: 'renta', name: 'Declaración de Renta' },
      { id: 'estado_dj', name: 'Estado de las Declaraciones Juradas' },
      { id: 'cuadre_formulario_29', name: 'Cuadre Formulario 29' },
    ]
  },
  {
    id: 'contabilidad',
    name: 'Contabilidad',
    subModules: [
      { id: 'balance_8_columnas', name: 'Balance 8 Columnas' },
      { id: 'balance_clasificado', name: 'Balance Clasificado' },
      { id: 'eerr_contable', name: 'Estado de Resultados Contable' },
      { id: 'libro_mayor', name: 'Libro Mayor' },
      { id: 'libro_diario', name: 'Libro Diario' },
      { id: 'asientos', name: 'Asientos Contables' },
      { id: 'plan_cuentas', name: 'Plan de Cuentas' },
      { id: 'centralizacion_contable', name: 'Centralización Contable' },
      { id: 'libro_compra_venta', name: 'Libro Compra/Venta' },
      { id: 'cierre_apertura_contable', name: 'Cierre/Apertura Contable' },
    ]
  },
  {
    id: 'recursos_humanos',
    name: 'Recursos Humanos',
    subModules: [
      { id: 'haberes_variables', name: 'Haberes Variables' },
      { id: 'cargar_nuevo_empleado', name: 'Cargar Nuevo Empleado' },
      { id: 'contratos_proximos_a_vencer', name: 'Contratos Próximos a Vencer' },
      { id: 'validador_liquidaciones', name: 'Validador de Liquidaciones' },
    ]
  },
  {
    id: 'repositorio',
    name: 'Repositorio',
    subModules: [
      { id: 'repositorio_informacion_inicial', name: 'Información Inicial' },
      { id: 'repositorio_informes_financieros', name: 'Informes Contables anuales' },
      { id: 'repositorio_presupuestos', name: 'Presupuestos' },
      { id: 'repositorio_certificados', name: 'Certificados' },
      { id: 'repositorio_formularios', name: 'Formularios' },
      { id: 'repositorio_libro_remuneraciones', name: 'Libros de Remuneraciones' },
      { id: 'repositorio_haberes_variables', name: 'Haberes Variables' },
      { id: 'repositorio_directorio', name: 'Informes Financieros Mensuales' },
      { id: 'repositorio_auditorias', name: 'Auditorías' },
      { id: 'repositorio_cartola_diaria', name: 'Cartola Diaria' },
      { id: 'repositorio_proveedores_hoja', name: 'Hoja Proveedores' },
      { id: 'repositorio_flujo_caja_mensual', name: 'Flujo de Caja Mensual' },
      { id: 'repositorio_flujo_caja_quincenal', name: 'Flujo de Caja Quincenal' },
      { id: 'repositorio_flujo_caja_semanal', name: 'Flujo de Caja Semanal' },
    ]
  },
  {
    id: 'seguimiento',
    name: 'Seguimiento',
    subModules: [
      { id: 'ficha_cliente', name: 'Ficha Cliente' },
      { id: 'avance_contable', name: 'Estado de Avance' },
    ]
  },
  {
    id: 'ajustes',
    name: 'Ajustes',
    subModules: [
      { id: 'conexiones', name: 'Conexiones' },
      { id: 'agenda', name: 'Contactos' },
      { id: 'configuracion', name: 'Configuración' },
      { id: 'configuracion_equipo', name: 'Equipo (configuración)' },
      { id: 'configuracion_api_key', name: 'API Key (configuración)' },
      { id: 'configuracion_automatizaciones', name: 'Automatizaciones (configuración)' },
      { id: 'descargas', name: 'Descargas' },
    ]
  },
]

// Mapa de moduleId → nombre legible (generado desde MODULE_CATEGORIES + grants)
const MODULE_NAME_MAP = (() => {
  const map = { ...MODULE_GRANT_LABELS }
  MODULE_CATEGORIES.forEach(cat => {
    map[cat.id] = cat.name
    cat.subModules.forEach(sub => {
      map[sub.id] = sub.name
    })
  })
  return map
})()

// Obtener nombre legible de un módulo por su ID
const getModuleName = (moduleId) => MODULE_NAME_MAP[moduleId] || moduleId

// Componente expandible para cada entrada del historial de permisos
function PermissionHistoryItem({ change, hasDetails, originalIndex, currentUserEmail, onSaveComment, onDeleteComment, formatDateTime }) {
  const [expanded, setExpanded] = useState(false)
  const hasModuleDetails = (change.modulesAdded?.length > 0) || (change.modulesRemoved?.length > 0)

  // Determinar icono y color del badge igual que empresa
  const onlyAdded = change.modulesAdded?.length > 0 && !change.modulesRemoved?.length
  const isAdding = onlyAdded || (!hasModuleDetails && change.modulesRestricted === 0)
  const badgeBg = isAdding ? '#d1e7dd' : '#f8d7da'
  const badgeColor = isAdding ? '#198754' : '#dc3545'
  const badgeIcon = isAdding ? '➕' : '➖'

  // Texto del badge
  const badgeText = hasModuleDetails
    ? `${(change.modulesRemoved?.length || 0) + (change.modulesAdded?.length || 0)} módulo${((change.modulesRemoved?.length || 0) + (change.modulesAdded?.length || 0)) !== 1 ? 's' : ''}`
    : change.modulesRestricted > 0
      ? `${change.modulesRestricted} módulo${change.modulesRestricted !== 1 ? 's' : ''} restringido${change.modulesRestricted !== 1 ? 's' : ''}`
      : 'Acceso completo'

  return (
    <div className="history-item">
      <div className="history-date">{formatDateTime(change.changedAt)}</div>
      <div className="history-content">
        <span
          className="status-badge"
          style={{
            backgroundColor: badgeBg,
            color: badgeColor,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
          onClick={() => setExpanded(!expanded)}
          title="Clic para ver detalle"
        >
          {badgeIcon} {badgeText}
          <span style={{ fontSize: '0.7rem', marginLeft: '2px' }}>
            {expanded ? '▲' : '▼'}
          </span>
        </span>
      </div>

      {/* Detalle expandible de módulos modificados */}
      {expanded && (
        <div style={{
          marginTop: '6px',
          padding: '8px 10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          fontSize: '0.78rem',
          lineHeight: '1.6',
          border: '1px solid #e9ecef',
        }}>
          {hasModuleDetails ? (
            <>
              {change.modulesRemoved?.length > 0 && (
                <div style={{ marginBottom: change.modulesAdded?.length > 0 ? '6px' : 0 }}>
                  <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                    {change.modulesRemoved.map((moduleId) => (
                      <li key={moduleId} style={{ color: '#dc3545' }}>{getModuleName(moduleId)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {change.modulesAdded?.length > 0 && (
                <div>
                  <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                    {change.modulesAdded.map((moduleId) => (
                      <li key={moduleId} style={{ color: '#198754' }}>{getModuleName(moduleId)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
              (Detalle de módulos no disponible para registros antiguos)
            </span>
          )}
        </div>
      )}

      <div className="history-user">Por: {change.changedByEmail}</div>
      <HistoryComment
        comment={change.comment}
        commentAuthorEmail={change.commentAuthorEmail}
        currentUserEmail={currentUserEmail}
        onSave={onSaveComment}
        onDelete={onDeleteComment}
      />
    </div>
  )
}

// Componente expandible para cada entrada del historial de empresas
function CompanyHistoryItem({ change, originalIndex, currentUserEmail, onSaveComment, onDeleteComment, formatDateTime }) {
  const [expanded, setExpanded] = useState(false)
  const hasDetails = change.companyNames?.length > 0

  return (
    <div className="history-item">
      <div className="history-date">{formatDateTime(change.changedAt)}</div>
      <div className="history-content">
        <span
          className="status-badge"
          style={{
            backgroundColor: change.action === 'ADDED' ? '#d1e7dd' : '#f8d7da',
            color: change.action === 'ADDED' ? '#198754' : '#dc3545',
            cursor: hasDetails ? 'pointer' : 'default',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
          onClick={() => hasDetails && setExpanded(!expanded)}
          title={hasDetails ? 'Clic para ver detalle de empresas' : ''}
        >
          {change.action === 'ADDED' ? '➕' : '➖'} {change.companiesCount} empresa{change.companiesCount > 1 ? 's' : ''}
          {hasDetails && (
            <span style={{ fontSize: '0.7rem', marginLeft: '2px' }}>
              {expanded ? '▲' : '▼'}
            </span>
          )}
        </span>
      </div>

      {/* Detalle expandible de empresas */}
      {expanded && hasDetails && (
        <div style={{
          marginTop: '6px',
          padding: '8px 10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          fontSize: '0.78rem',
          lineHeight: '1.6',
          border: '1px solid #e9ecef',
        }}>
          <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
            {change.companyNames.map((name, i) => (
              <li key={i} style={{ color: change.action === 'ADDED' ? '#198754' : '#dc3545' }}>{name}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="history-user">Por: {change.changedByEmail}</div>
      <HistoryComment
        comment={change.comment}
        commentAuthorEmail={change.commentAuthorEmail}
        currentUserEmail={currentUserEmail}
        onSave={onSaveComment}
        onDelete={onDeleteComment}
      />
    </div>
  )
}

// Modal de Permisos Granulares
function PermissionsModal({ user, isOpen, onClose, onSave }) {
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({}) // Categorías expandidas
  /** Snapshot al abrir el modal; solo se persisten filas que difieren al guardar */
  const initialPermissionsRef = useRef([])

  // Obtener todos los IDs de módulos (categorías + sub-módulos)
  const getAllModuleIds = () => {
    const ids = []
    MODULE_CATEGORIES.forEach(cat => {
      if (!isRepositorioParentModuleId(cat.id)) ids.push(cat.id)
      cat.subModules.forEach(sub => ids.push(sub.id))
    })
    return ids
  }

  useEffect(() => {
    if (isOpen && user) {
      const allModuleIds = getAllModuleIds()
      const initialPermissions = allModuleIds.map(moduleId => {
        const existingPerm = user.modulePermissions?.find(p => p.moduleId === moduleId)
        return {
          moduleId,
          hasAccess: resolveModuleHasAccess(existingPerm, moduleId, true, user.roles),
          excludedFeatures: [...(existingPerm?.excludedFeatures || [])],
        }
      })
      initialPermissionsRef.current = initialPermissions.map((p) => ({
        moduleId: p.moduleId,
        hasAccess: p.hasAccess,
        excludedFeatures: [...p.excludedFeatures],
      }))
      setPermissions(initialPermissions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user])

  // Toggle categoría principal (afecta todos sus sub-módulos)
  const toggleCategory = (categoryId, subModuleIds) => {
    setPermissions(prev => {
      const categoryPerm = prev.find(p => p.moduleId === categoryId)
      const newAccess = !(categoryPerm?.hasAccess ?? true)
      
      return prev.map(p => {
        if (p.moduleId === categoryId || subModuleIds.includes(p.moduleId)) {
          return { ...p, hasAccess: newAccess }
        }
        return p
      })
    })
  }

  // Toggle sub-módulo individual
  const toggleSubModule = (subModuleId) => {
    setPermissions(prev => 
      prev.map(p => 
        p.moduleId === subModuleId 
          ? { ...p, hasAccess: !p.hasAccess }
          : p
      )
    )
  }

  // Expandir/colapsar categoría
  const toggleExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  // Verificar si la categoría tiene acceso (todos sus sub-módulos tienen acceso)
  const getCategoryAccess = (categoryId, subModuleIds) => {
    const categoryPerm = permissions.find(p => p.moduleId === categoryId)
    if (subModuleIds.length === 0) {
      return categoryPerm?.hasAccess ?? true
    }
    // Si todos los sub-módulos tienen acceso, la categoría tiene acceso
    const allSubsHaveAccess = subModuleIds.every(id => {
      const perm = permissions.find(p => p.moduleId === id)
      return perm?.hasAccess ?? true
    })
    return allSubsHaveAccess
  }

  // Contar sub-módulos restringidos
  const countRestrictedSubs = (subModuleIds) => {
    return subModuleIds.filter(id => {
      const perm = permissions.find(p => p.moduleId === id)
      return perm?.hasAccess === false
    }).length
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      const initial = initialPermissionsRef.current
      const changed = permissions.filter((p) => {
        const init = initial.find((i) => i.moduleId === p.moduleId)
        if (!init) return true
        if (init.hasAccess !== p.hasAccess) return true
        const a = JSON.stringify(init.excludedFeatures || [])
        const b = JSON.stringify(p.excludedFeatures || [])
        return a !== b
      })
      await onSave(withoutRepositorioParentPermission(changed))
      onClose()
    } catch (err) {
      alert(err?.message || 'Error al guardar permisos')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content permissions-modal folder-style" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Permisos de Módulos</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="modal-description">
            Configura el acceso a módulos y funcionalidades específicas para este usuario. Al guardar, solo se
            actualizan en el servidor los módulos que modificaste respecto al estado al abrir este cuadro.
          </p>
          
          <div className="folder-tree">
            {MODULE_CATEGORIES.map(category => {
              const subModuleIds = category.subModules.map(s => s.id)
              const hasSubModules = subModuleIds.length > 0
              const isExpanded = expandedCategories[category.id]
              const categoryHasAccess = getCategoryAccess(category.id, subModuleIds)
              const restrictedCount = countRestrictedSubs(subModuleIds)
              
              return (
                <div key={category.id} className="folder-item">
                  {/* Carpeta principal */}
                  <div className={`folder-header ${!categoryHasAccess ? 'disabled' : ''}`}>
                    {hasSubModules ? (
                      <button 
                        className="folder-expand-btn"
                        onClick={() => toggleExpanded(category.id)}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    ) : (
                      <span className="folder-expand-placeholder"></span>
                    )}
                    <span className="folder-name" onClick={() => hasSubModules && toggleExpanded(category.id)}>
                      {category.name}
                    </span>
                    {hasSubModules && restrictedCount > 0 && (
                      <span className="restricted-badge">
                        {restrictedCount}/{subModuleIds.length} restringido{restrictedCount > 1 ? 's' : ''}
                      </span>
                    )}
                    {!isRepositorioParentModuleId(category.id) && (
                    <label className="folder-toggle">
                      <input
                        type="checkbox"
                        checked={categoryHasAccess}
                        onChange={() => toggleCategory(category.id, subModuleIds)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    )}
                  </div>
                  
                  {/* Sub-módulos */}
                  {hasSubModules && isExpanded && (
                    <div className="folder-children">
                      {category.subModules.map(subMod => {
                        const subPerm = permissions.find(p => p.moduleId === subMod.id)
                        const subHasAccess = subPerm?.hasAccess ?? true
                        
                        return (
                          <div key={subMod.id} className={`folder-child ${!subHasAccess ? 'disabled' : ''}`}>
                            <span className="child-name">{subMod.name}</span>
                            <label className="folder-toggle small">
                              <input
                                type="checkbox"
                                checked={subHasAccess}
                                onChange={() => toggleSubModule(subMod.id)}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="action-btn secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button className="action-btn primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Permisos'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal para gestionar empresas del usuario
function ManageCompaniesModal({ user, isOpen, onClose, onSuccess }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCompanies, setSelectedCompanies] = useState([])
  const [mode, setMode] = useState('add') // 'add' o 'remove'
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Cargar todas las empresas
  const [{ data: companiesData, fetching: fetchingCompanies }] = useQuery({
    query: GET_COMPANIES,
    variables: { filter: { pageSize: 500 } },
    pause: !isOpen,
    requestPolicy: 'network-only',
  })

  // Cargar empresas donde el usuario está asociado
  const [{ data: userCompaniesData, fetching: fetchingUserCompanies }, refetchUserCompanies] = useQuery({
    query: GET_COMPANIES_BY_USER,
    variables: { userId: user?._id || user?.id },
    pause: !isOpen || !user,
    requestPolicy: 'network-only',
  })

  const [, bulkAddUser] = useMutation(BULK_ADD_USER_TO_COMPANIES)
  const [, bulkRemoveUser] = useMutation(BULK_REMOVE_USER_FROM_COMPANIES)

  const allCompanies = companiesData?.companies?.items || []
  const userCompanies = userCompaniesData?.companiesByUserId || []
  
  // Obtener IDs de empresas donde ya está el usuario
  const userCompanyIds = userCompanies.map(c => c._id)

  // Filtrar empresas según búsqueda y modo
  const filteredCompanies = (mode === 'add' ? allCompanies : userCompanies).filter(company => {
    const matchesSearch = !searchTerm || 
      company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.rut?.toLowerCase().includes(searchTerm.toLowerCase())
    
    if (mode === 'add') {
      // Mostrar empresas donde NO está el usuario
      return matchesSearch && !userCompanyIds.includes(company._id)
    } else {
      // Mostrar empresas donde SÍ está el usuario (ya filtradas por userCompanies)
      return matchesSearch
    }
  })

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanies([])
      setSearchTerm('')
      setSuccessMessage('')
    }
  }, [isOpen, mode])

  const toggleCompany = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    )
  }

  const selectAll = () => {
    setSelectedCompanies(filteredCompanies.map(c => c._id))
  }

  const clearSelection = () => {
    setSelectedCompanies([])
  }

  const handleSubmit = async () => {
    if (selectedCompanies.length === 0) return

    setLoading(true)
    setSuccessMessage('')

    try {
      // Usar el prop user para obtener el ID
      const userIdToUse = user?._id || user?.id

      if (!userIdToUse) {
        throw new Error('ID de usuario no disponible')
      }

      const input = {
        userId: userIdToUse,
        companyIds: selectedCompanies,
        role: 'USER',
      }

      let result
      if (mode === 'add') {
        result = await bulkAddUser({ input })
      } else {
        result = await bulkRemoveUser({ input: { userId: userIdToUse, companyIds: selectedCompanies } })
      }

      if (result.error) {
        throw result.error
      }

      const count = selectedCompanies.length
      setSuccessMessage(
        mode === 'add' 
          ? `✓ Usuario agregado a ${count} empresa${count > 1 ? 's' : ''}`
          : `✓ Usuario removido de ${count} empresa${count > 1 ? 's' : ''}`
      )
      setSelectedCompanies([])
      
      // Refrescar lista de empresas del usuario
      refetchUserCompanies({ requestPolicy: 'network-only' })
      
      // Notificar éxito para refrescar datos
      setTimeout(() => {
        onSuccess()
      }, 1500)

    } catch (err) {
      setSuccessMessage('❌ Error: ' + (err.message || 'Error desconocido'))
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content companies-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🏢 Gestionar Empresas</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Tabs de modo */}
          <div className="mode-tabs">
            <button 
              className={`mode-tab ${mode === 'add' ? 'active' : ''}`}
              onClick={() => setMode('add')}
            >
              ➕ Agregar a Empresas
            </button>
            <button 
              className={`mode-tab ${mode === 'remove' ? 'active' : ''}`}
              onClick={() => setMode('remove')}
            >
              ➖ Quitar de Empresas
            </button>
          </div>

          {/* Buscador */}
          <div className="search-section">
            <input
              type="text"
              placeholder="Buscar empresa por nombre o RUT..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Acciones de selección */}
          <div className="selection-actions">
            <span className="selection-count">
              {selectedCompanies.length} seleccionada{selectedCompanies.length !== 1 ? 's' : ''}
            </span>
            <div className="selection-buttons">
              <button className="text-btn" onClick={selectAll}>Seleccionar todas</button>
              <button className="text-btn" onClick={clearSelection}>Limpiar</button>
            </div>
          </div>

          {/* Mensaje de éxito/error */}
          {successMessage && (
            <div className={`alert ${successMessage.startsWith('✓') ? 'success' : 'error'}`}>
              {successMessage}
            </div>
          )}

          {/* Lista de empresas */}
          <div className="companies-list">
            {(fetchingCompanies || fetchingUserCompanies) ? (
              <div className="loading-small">Cargando empresas...</div>
            ) : filteredCompanies.length === 0 ? (
              <div className="empty-state-small">
                {mode === 'add' 
                  ? 'No hay empresas disponibles para agregar'
                  : 'El usuario no está en ninguna empresa'}
              </div>
            ) : (
              filteredCompanies.map(company => {
                const isActive = company.adminStatus !== 'BLOQUEADO'
                return (
                <label key={company._id} className={`company-item ${selectedCompanies.includes(company._id) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedCompanies.includes(company._id)}
                    onChange={() => toggleCompany(company._id)}
                  />
                  <div className="company-info">
                    <span className="company-name">{company.name}</span>
                    <span className="company-rut">{company.rut || 'Sin RUT'}</span>
                  </div>
                  <span className={`status-indicator ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? '●' : '○'}
                  </span>
                </label>
                )
              })
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="action-btn secondary" onClick={onClose} disabled={loading}>
            Cerrar
          </button>
          <button 
            className={`action-btn ${mode === 'add' ? 'primary' : 'danger'}`}
            onClick={handleSubmit}
            disabled={loading || selectedCompanies.length === 0}
          >
            {loading 
              ? 'Procesando...' 
              : mode === 'add' 
                ? `Agregar a ${selectedCompanies.length} empresa${selectedCompanies.length !== 1 ? 's' : ''}`
                : `Quitar de ${selectedCompanies.length} empresa${selectedCompanies.length !== 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  )
}

export function UserDetail({ userId, onBack, currentUserEmail }) {
  if (!userId) {
    throw new Error('userId prop is required but not provided.');
  }

  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [showCompaniesModal, setShowCompaniesModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showFunctionsModal, setShowFunctionsModal] = useState(false)
  
  const [result, reexecuteQuery] = useQuery({
    query: GET_USER,
    variables: { id: userId },
    requestPolicy: 'network-only',
  })

  const [modulesResult] = useQuery({
    query: GET_AVAILABLE_MODULES,
  })

  // Query para obtener empresas del usuario - siempre desde network
  const [userCompaniesResult, reexecuteCompaniesQuery] = useQuery({
    query: GET_COMPANIES_BY_USER,
    variables: { userId },
    requestPolicy: 'network-only',
  })

  const [, updateModulePermission] = useMutation(UPDATE_MODULE_PERMISSION)
  const [, updateHistoryComment] = useMutation(UPDATE_USER_HISTORY_COMMENT)
  const [, toggleUserActive] = useMutation(TOGGLE_USER_ACTIVE)
  const [, deleteUser] = useMutation(DELETE_USER)
  const [, updateBlockedFeatures] = useMutation(UPDATE_USER_BLOCKED_FEATURES)
  const [, deleteHistoryComment] = useMutation(DELETE_USER_HISTORY_COMMENT)

  const { data, fetching, error } = result
  const user = data?.user
  // eslint-disable-next-line no-unused-vars
  const availableModules = modulesResult.data?.availableModules || []
  const userCompanies = userCompaniesResult.data?.companiesByUserId || []



  const handleDeleteUser = async (reason) => {
    if (!user) return
    
    try {
      const result = await deleteUser({
        input: {
          userId: user._id,
          reason: reason || undefined,
        },
      })
      
      if (result.error) {
        alert(`Error: ${result.error.message}`)
        return
      }
      
      if (result.data?.deleteUser?.success) {
        alert('Usuario eliminado exitosamente')
        setShowStatusModal(false)
        onBack() // Volver a la lista de usuarios
      } else {
        alert(`Error: ${result.data?.deleteUser?.message || 'Error desconocido'}`)
      }
    } catch (error) {
      alert(`Error al eliminar usuario: ${error.message}`)
    }
  }

  const handleChangeStatus = async (newStatus) => {
    if (!user) return
    
    const isActivating = newStatus === 'ACTIVO'
    
    try {
      const result = await toggleUserActive({
        input: {
          userId: user._id,
          isActive: isActivating,
        },
      })
      
      if (result.error) {
        alert(`Error: ${result.error.message}`)
      } else {
        // Refrescar datos del usuario
        reexecuteQuery({ requestPolicy: 'network-only' })
        setShowStatusModal(false)
      }
    } catch (error) {
      alert(`Error al cambiar estado: ${error.message}`)
    }
  }

  /**
   * @param {Array<{ moduleId: string, hasAccess: boolean, excludedFeatures?: string[] }>} patches Solo módulos cambiados vs. snapshot al abrir el modal
   */
  const handleSavePermissions = async (patches) => {
    const userIdToUse = user?._id || userId

    if (!userIdToUse) {
      return
    }

    if (!patches?.length) {
      reexecuteQuery({ requestPolicy: 'network-only' })
      return
    }

    // Secuencial: el backend hace read-merge-write por módulo; en paralelo se podría pisar estado.
    for (const p of patches) {
      const result = await updateModulePermission({
        input: {
          userId: userIdToUse,
          moduleId: p.moduleId,
          hasAccess: p.hasAccess,
          excludedFeatures: p.excludedFeatures || [],
        },
      })

      if (result.error) {
        throw result.error
      }
    }

    reexecuteQuery({ requestPolicy: 'network-only' })
  }

  const handleUpdateBlockedFeatures = async ({ blockedFeatures, moduleGrantPatches = [] }) => {
    try {
      const result = await updateBlockedFeatures({
        input: {
          userId: userId,
          blockedFeatures: blockedFeatures,
        },
      })
      
      if (result.error) {
        const msg =
          result.error.graphQLErrors?.[0]?.message ||
          result.error.message ||
          'Error al actualizar permisos de funciones.'
        console.error('Error al actualizar funcionalidades bloqueadas:', result.error)
        alert(msg)
        return false
      }

      for (const p of moduleGrantPatches) {
        if (!isModuleGrantModule(p.moduleId)) continue
        const grantResult = await updateModulePermission({
          input: {
            userId: userId,
            moduleId: p.moduleId,
            hasAccess: p.hasAccess,
            excludedFeatures: p.excludedFeatures || [],
          },
        })
        if (grantResult.error) {
          throw grantResult.error
        }
      }
      
      reexecuteQuery({ requestPolicy: 'network-only' })
      return true
    } catch (error) {
      console.error('Error al actualizar funcionalidades bloqueadas:', error)
      alert('Error al actualizar. Por favor, intenta de nuevo.')
      return false
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Función para guardar comentarios de historial
  const handleSaveHistoryComment = async (historyType, changedAt, comment, index) => {
    try {
      const result = await updateHistoryComment({
        input: {
          userId: user._id,
          historyType,
          changedAt: new Date(changedAt).toISOString(),
          comment: comment || '',
          index,
        },
      })
      
      if (result.error) {
        console.error('Error al guardar comentario:', result.error)
        alert('Error al guardar el comentario. Por favor, intenta de nuevo.')
        return
      }
      
      // Recargar en background sin bloquear UI
      setTimeout(() => {
        reexecuteQuery({ requestPolicy: 'network-only' })
      }, 0)
    } catch (error) {
      console.error('Error al guardar comentario:', error)
      alert('Error al guardar el comentario. Por favor, intenta de nuevo.')
    }
  }

  // Función para eliminar comentarios de historial
  const handleDeleteHistoryComment = async (historyType, changedAt, index) => {
    try {
      const result = await deleteHistoryComment({
        input: {
          userId: user._id,
          historyType,
          changedAt: new Date(changedAt).toISOString(),
          index,
        },
      })
      
      if (result.error) {
        console.error('Error al eliminar comentario:', result.error)
        alert('Error al eliminar el comentario. Por favor, intenta de nuevo.')
        return
      }
      
      // Recargar en background sin bloquear UI
      setTimeout(() => {
        reexecuteQuery({ requestPolicy: 'network-only' })
      }, 0)
    } catch (error) {
      console.error('Error al eliminar comentario:', error)
      alert('Error al eliminar el comentario. Por favor, intenta de nuevo.')
    }
  }

  if (fetching) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="content-card">
        <div className="card-body">
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <p>Error al cargar el usuario</p>
            <button className="action-btn secondary" onClick={onBack}>
              Volver
            </button>
          </div>
        </div>
      </div>
    )
  }

  const primaryEmail = user.emails?.[0]?.address || 'Sin email'

  return (
    <>
      {/* Header */}
      <div className="detail-header">
        <div className="detail-title-section">
          <button className="back-btn" onClick={onBack}>
            ←
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1 className="page-title" style={{ margin: 0 }}>{primaryEmail}</h1>
              <StatusBadge status={user.adminStatus || 'ACTIVO'} />
            </div>
            <p className="page-subtitle" style={{ marginTop: '0.25rem' }}>
              {user.profile?.firstName || ''} {user.profile?.lastName || ''}
            </p>
          </div>
        </div>
      </div>

      {/* Info General */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Información General</h3>
        </div>
        <div className="card-body">
          <div className="detail-info">
            <div className="info-item">
              <span className="info-label">Nombre</span>
              <span className="info-value">
                {user.profile?.firstName || '-'} {user.profile?.lastName || ''}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha de Creación</span>
              <span className="info-value">{formatDate(user.createdAt)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Admin SmartCFO</span>
              <span className="info-value">
                {user.isSmartCfoAdmin ? (
                  <span className="role-badge admin">Sí</span>
                ) : (
                  'No'
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Emails */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Emails ({user.emails?.length || 0})</h3>
        </div>
        <div className="card-body">
          {user.emails?.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Verificado</th>
                </tr>
              </thead>
              <tbody>
                {user.emails.map((email, index) => (
                  <tr key={index}>
                    <td>{email.address}</td>
                    <td>
                      {email.verified ? (
                        <span className="status-badge activo">
                          <span className="status-dot"></span>
                          Verificado
                        </span>
                      ) : (
                        <span className="status-badge inactive">
                          <span className="status-dot"></span>
                          No verificado
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📧</div>
              <p>No hay emails registrados</p>
            </div>
          )}
        </div>
      </div>

      {/* Rol del Usuario */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Rol</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {(() => {
              const roles = user.roles || [];
              if (roles.includes('ADMIN')) return <span className="role-badge admin" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>Admin</span>;
              if (roles.includes('DPO')) return <span className="role-badge admin" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>DPO</span>;
              if (roles.includes('SUPER_COLABORADOR')) return <span className="role-badge colaborador" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>Super Colaborador</span>;
              if (roles.includes('COLABORADOR')) return <span className="role-badge colaborador" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>Colaborador</span>;
              if (roles.includes('USER_SMARTCFO')) return <span className="role-badge user" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>Usuario</span>;
              return <span style={{ color: '#7f8c8d' }}>Sin rol asignado</span>;
            })()}
          </div>
        </div>
      </div>

      {/* Gestión de Empresas */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">🏢 Empresas</h3>
          {/* Mostrar botón de gestión solo para usuarios normales (no ADMIN, SUPER_COLABORADOR ni COLABORADOR) */}
          {!user.roles?.includes('ADMIN') && !user.roles?.includes('SUPER_COLABORADOR') && !user.roles?.includes('DPO') && !user.roles?.includes('COLABORADOR') && (
            <button 
              className="action-btn primary"
              onClick={() => setShowCompaniesModal(true)}
            >
              Gestionar Empresas
            </button>
          )}
        </div>
        <div className="card-body">
          {(user.roles?.includes('ADMIN') || user.roles?.includes('SUPER_COLABORADOR') || user.roles?.includes('DPO')) ? (
            <div className="privileged-access-notice">
              <div className="privilege-badge">
                <span className="privilege-icon">👑</span>
                <div className="privilege-info">
                  <span className="privilege-title">Acceso Privilegiado</span>
                  <span className="privilege-desc">
                    Este usuario tiene rol <strong>{user.roles?.includes('ADMIN') ? 'ADMIN' : user.roles?.includes('DPO') ? 'DPO' : 'SUPER_COLABORADOR'}</strong> y puede ver <strong>todas las empresas</strong> del sistema automáticamente.
                  </span>
                </div>
              </div>
              <p className="privilege-note">
                No es necesario asignarlo a empresas específicas. Si deseas restringir su acceso, primero debes cambiar su rol.
              </p>
            </div>
          ) : user.roles?.includes('COLABORADOR') ? (
            <>
              <div className="privileged-access-notice collaborator">
                <div className="privilege-badge">
                  <span className="privilege-icon">🔓</span>
                  <div className="privilege-info">
                    <span className="privilege-title">Acceso Colaborador</span>
                    <span className="privilege-desc">
                      Este usuario tiene rol <strong>COLABORADOR</strong> y su acceso a empresas está definido por la configuración del sistema.
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="companies-count-info">
                <span className="count-number">{userCompanies.length}</span>
                <span className="count-label">empresa{userCompanies.length !== 1 ? 's' : ''} asignada{userCompanies.length !== 1 ? 's' : ''}</span>
              </div>
              {userCompanies.length > 0 ? (
                <div className="companies-summary">
                  {userCompanies.slice(0, 10).map((company, idx) => (
                    <div key={company._id || idx} className="company-summary-item">
                      <span className="company-name">{company.name || 'Sin nombre'}</span>
                      <span className="company-rut">{company.rut || ''}</span>
                    </div>
                  ))}
                  {userCompanies.length > 10 && (
                    <div className="more-companies">
                      +{userCompanies.length - 10} empresas más
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state-small warning">
                  <span>⚠️ El usuario no está asociado a ninguna empresa y no podrá ver ninguna en el portal.</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Permisos de Módulos y Permisos de Funciones - Lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Permisos de Módulos */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Permisos de Módulos</h3>
            <button 
              className="action-btn primary"
              onClick={() => setShowPermissionsModal(true)}
            >
              Configurar Permisos
            </button>
          </div>
          <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <div className="permissions-summary">
              {MODULE_CATEGORIES.map((cat) => {
                const modules = cat.subModules.length > 0 ? cat.subModules : [{ id: cat.id, name: cat.name }]
                return (
                  <div key={cat.id} style={{ marginBottom: '0.5rem' }}>
                    {cat.subModules.length > 0 && (
                      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>{cat.name}</div>
                    )}
                    {modules.map((mod) => {
                      const perm = user.modulePermissions?.find(p => p.moduleId === mod.id)
                      const hasAccess = resolveModuleHasAccess(perm, mod.id, true, user.roles)
                      return (
                        <div key={mod.id} className="permission-summary-item">
                          <span className="permission-module-name" style={!hasAccess ? { color: '#dc2626' } : undefined}>
                            {mod.name}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Permisos de Funciones */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Permisos de Funciones</h3>
            <button 
              className="action-btn primary"
              onClick={() => setShowFunctionsModal(true)}
            >
              Configurar Permisos
            </button>
          </div>
          <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <div className="permissions-summary">
              {USER_BLOCKABLE_FEATURES.map((feature) => {
                const grantedByRole = featureGrantedByRole(feature, user?.roles)
                const parentModuleBlocked =
                  !!feature.parentModule && !hasParentModuleAccess(feature.parentModule, user)
                const hasAccess = featureHasAccess(feature, user?.blockedFeatures, user?.roles, user)
                return (
                  <div key={feature.id} className="permission-summary-item">
                    <span
                      className="permission-module-name"
                      style={!hasAccess ? { color: '#dc2626' } : undefined}
                    >
                      {featureDisplayName(feature)}
                      {parentModuleBlocked
                        ? ' (sin módulo)'
                        : grantedByRole
                          ? ' (por rol)'
                          : !hasAccess
                            ? ' (sin acceso)'
                            : ''}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Historiales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
        {/* Historial de Estados */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Historial de Estados</h3>
          </div>
          <div className="card-body" style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {user.statusHistory?.length > 0 ? (
              <div className="history-timeline">
                {user.statusHistory
                  .slice()
                  .reverse()
                  .map((change, reversedIndex) => {
                    const originalIndex = user.statusHistory.length - 1 - reversedIndex
                    return (
                      <div key={reversedIndex} className="history-item">
                        <div className="history-date">{formatDateTime(change.changedAt)}</div>
                        <div className="history-content">
                          <StatusBadge status={change.previousStatus} size="small" />
                          <span style={{ margin: '0 0.5rem' }}>→</span>
                          <StatusBadge status={change.newStatus} size="small" />
                        </div>
                        <div className="history-user">Por: {change.changedByEmail}</div>
                        <HistoryComment 
                          comment={change.comment}
                          commentAuthorEmail={change.commentAuthorEmail}
                          currentUserEmail={currentUserEmail}
                          onSave={(comment) => handleSaveHistoryComment('status', change.changedAt, comment, originalIndex)}
                          onDelete={() => handleDeleteHistoryComment('status', change.changedAt, originalIndex)}
                        />
                      </div>
                    )
                  })}
              </div>
            ) : (
              <div className="empty-state-small">
                <span>Sin cambios registrados</span>
              </div>
            )}
          </div>
        </div>

        {/* Historial de Roles */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Historial de Roles</h3>
          </div>
          <div className="card-body" style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {user.roleHistory?.length > 0 ? (
              <div className="history-timeline">
                {user.roleHistory
                  .slice()
                  .reverse()
                  .map((change, index) => {
                    const getRoleBadge = (roles) => {
                      if (!roles || roles.length === 0) return 'Sin rol'
                      if (roles.includes('ADMIN')) return 'Admin'
                      if (roles.includes('DPO')) return 'DPO'
                      if (roles.includes('SUPER_COLABORADOR')) return 'Super Colaborador'
                      if (roles.includes('COLABORADOR')) return 'Colaborador'
                      if (roles.includes('USER_SMARTCFO')) return 'Usuario'
                      return roles[0]
                    }
                    
                    return (
                      <div key={index} className="history-item">
                        <div className="history-date">{formatDateTime(change.changedAt)}</div>
                        <div className="history-content">
                          <span className="status-badge" style={{ backgroundColor: '#e8f4f8', color: '#0d6efd' }}>
                            {getRoleBadge(change.previousRoles)}
                          </span>
                          <span style={{ margin: '0 0.5rem' }}>→</span>
                          <span className="status-badge" style={{ backgroundColor: '#d1e7dd', color: '#198754' }}>
                            {getRoleBadge(change.newRoles)}
                          </span>
                        </div>
                        <div className="history-user">Por: {change.changedByEmail}</div>
                        <HistoryComment 
                          comment={change.comment}
                          commentAuthorEmail={change.commentAuthorEmail}
                          currentUserEmail={currentUserEmail}
                          onSave={(comment) => {
                            const originalIndex = user.roleHistory.length - 1 - index
                            handleSaveHistoryComment('role', change.changedAt, comment, originalIndex)
                          }}
                          onDelete={() => {
                            const originalIndex = user.roleHistory.length - 1 - index
                            handleDeleteHistoryComment('role', change.changedAt, originalIndex)
                          }}
                        />
                      </div>
                    )
                  })}
              </div>
            ) : (
              <div className="empty-state-small">
                <span>Sin cambios registrados</span>
              </div>
            )}
          </div>
        </div>

        {/* Historial de Permisos */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Historial de Permisos</h3>
          </div>
          <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {user.permissionHistory?.length > 0 ? (
              <div className="history-timeline">
                {user.permissionHistory
                  .slice()
                  .reverse()
                  .map((change, reversedIndex) => {
                    const originalIndex = user.permissionHistory.length - 1 - reversedIndex
                    const hasDetails = (change.modulesAdded?.length > 0) || (change.modulesRemoved?.length > 0)
                    return (
                      <PermissionHistoryItem
                        key={reversedIndex}
                        change={change}
                        hasDetails={hasDetails}
                        originalIndex={originalIndex}
                        currentUserEmail={currentUserEmail}
                        onSaveComment={(comment) => handleSaveHistoryComment('permission', change.changedAt, comment, originalIndex)}
                        onDeleteComment={() => handleDeleteHistoryComment('permission', change.changedAt, originalIndex)}
                        formatDateTime={formatDateTime}
                      />
                    )
                  })}
              </div>
            ) : (
              <div className="empty-state-small">
                <span>Sin cambios registrados</span>
              </div>
            )}
          </div>
        </div>

        {/* Historial de Empresas */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Historial de Empresas</h3>
          </div>
          <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {user.companyHistory?.length > 0 ? (
              <div className="history-timeline">
                {user.companyHistory
                  .slice()
                  .reverse()
                  .map((change, reversedIndex) => {
                    const originalIndex = user.companyHistory.length - 1 - reversedIndex
                    return (
                      <CompanyHistoryItem
                        key={reversedIndex}
                        change={change}
                        originalIndex={originalIndex}
                        currentUserEmail={currentUserEmail}
                        onSaveComment={(comment) => handleSaveHistoryComment('company', change.changedAt, comment, originalIndex)}
                        onDeleteComment={() => handleDeleteHistoryComment('company', change.changedAt, originalIndex)}
                        formatDateTime={formatDateTime}
                      />
                    )
                  })}
              </div>
            ) : (
              <div className="empty-state-small">
                <span>Sin cambios registrados</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Permisos de Módulos */}
      <PermissionsModal
        user={user}
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        onSave={handleSavePermissions}
      />

      {/* Modal de Permisos de Funciones */}
      <FunctionsPermissionsModal
        user={user}
        isOpen={showFunctionsModal}
        onClose={() => setShowFunctionsModal(false)}
        onSave={handleUpdateBlockedFeatures}
      />

      {/* Modal de Gestión de Empresas */}
      <ManageCompaniesModal
        user={user}
        isOpen={showCompaniesModal}
        onClose={() => setShowCompaniesModal(false)}
        onSuccess={() => {
          setShowCompaniesModal(false)
          // Refrescar tanto el usuario como sus empresas
          reexecuteQuery({ requestPolicy: 'network-only' })
          reexecuteCompaniesQuery({ requestPolicy: 'network-only' })
        }}
      />

      {/* Modal de Cambiar Estado y Eliminar */}
      {showStatusModal && (
        <UserStatusModal
          user={user}
          onClose={() => setShowStatusModal(false)}
          onConfirm={handleChangeStatus}
          onDelete={handleDeleteUser}
        />
      )}
    </>
  )
}

export default UserDetail
